import {announce} from '@github-ui/aria-live'
import {addUrlToHistoryStack} from '@github-ui/history'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Type-only import of the shared deletion ownership contract.
import type {IssueDeletionConnection} from '@github-ui/issue-viewer/OptionConfig'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This mutation-cache observer has no Relay dependency.
import {useIssueDeletionState} from '@github-ui/issue-viewer/useIssueDeletionState'
import {
  normalizeSidePanelIssuePermalink,
  parseSidePanelIssue,
  serializeSidePanelIssue,
  type SidePanelIssue,
  SIDE_PANEL_SEARCH_PARAM,
  toSidePanelIssue,
} from '@github-ui/issues-shared/side-panel-url'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {DashboardIssueSidePanel} from '../components/DashboardIssueSidePanel'
import type {OnCreateProps} from '../components/issue-create-types'
import {focusParentListTarget} from './issue-viewer-side-panel-focus'
import type {
  DashboardIssueDeleteOptions,
  DashboardIssueDeletionConnection,
} from './use-dashboard-issue-optimistic-updates'

// The open side-panel issue is mirrored to `?issue=owner|repo|number` or its account reference so
// it's shareable and browser back/forward opens/closes the panel, matching the issues dashboard.
const SIDE_PANEL_GLOBAL_ID_PARAM = 'issue_global_id'
type DashboardSidePanelIssue = SidePanelIssue & {globalId?: string}
const DELETE_REJECTED_MESSAGE = 'Could not delete the issue. The issue was restored. Please try again.'
const DELETE_COMPLETION_MESSAGE =
  'The issue was deleted, but the dashboard could not be refreshed. Reload the page to recover.'
// Back/forward recovery only needs recent drilled-in panes; evicted entries fall back to the dashboard connection.
const MAX_REMEMBERED_DELETION_CONNECTIONS = 25

function parseSidePanelIssueFromUrl(): DashboardSidePanelIssue | null {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  const issue = parseSidePanelIssue(url.toString())
  if (!issue) return null
  const globalId = url.searchParams.get(SIDE_PANEL_GLOBAL_ID_PARAM) || undefined
  return {...issue, globalId}
}

function writeSidePanelIssueToUrl(identifier: DashboardSidePanelIssue | null): void {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  if (identifier) {
    const reference = serializeSidePanelIssue(identifier)
    if (reference) url.searchParams.set(SIDE_PANEL_SEARCH_PARAM, reference)
    else url.searchParams.delete(SIDE_PANEL_SEARCH_PARAM)

    if (identifier.globalId) {
      url.searchParams.set(SIDE_PANEL_GLOBAL_ID_PARAM, identifier.globalId)
    } else {
      url.searchParams.delete(SIDE_PANEL_GLOBAL_ID_PARAM)
    }
  } else {
    url.searchParams.delete(SIDE_PANEL_SEARCH_PARAM)
    url.searchParams.delete(SIDE_PANEL_GLOBAL_ID_PARAM)
  }
  addUrlToHistoryStack(url.toString())
}

function sidePanelIssueKey(identifier: SidePanelIssue): string {
  return identifier.isAccountIssue
    ? (normalizeSidePanelIssuePermalink(identifier.permalink) ?? identifier.permalink)
    : `${identifier.owner}/${identifier.repo}/${identifier.number}`
}

interface UseDashboardIssueSidePanelParams {
  /** Navigable issues in list order for j/k navigation. PRs are excluded so nav skips them. */
  navigableIssues: DashboardIssueIdentifier[]
  /** Refetches the list after edits when the panel closes. */
  refreshList: () => void
  /** Lets the panel's delete remove the row from this list while the request is in flight. */
  issueDeletionConnection?: DashboardIssueDeletionConnection
}

/**
 * Spread onto the dashboard list container, which takes focus when the panel closes and the row it
 * opened from is gone, or was never clicked.
 */
export type DashboardListContainerProps = {
  ref?: RefObject<HTMLDivElement | null>
  role?: 'region'
  'aria-label'?: string
}

interface UseDashboardIssueSidePanelResult {
  /** Row-click handler for `IssuesList`. */
  onIssueSelect: (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => void
  /**
   * Handles a created issue from a "New issue" flow. "Create more" keeps the dialog open so it just
   * refreshes the list; otherwise it opens the issue in the panel (refreshing so the new row appears
   * and is j/k-navigable, and focusing the list container on close).
   */
  onIssueCreated: (event: OnCreateProps) => void
  /**
   * True while the panel is showing an issue. The panel binds `j`/`k` to step between issues, so
   * the list below must stop handling those keys for as long as this is true.
   */
  isSidePanelOpen: boolean
  /** The issue currently open in the panel, retained across list page/filter changes. */
  sidePanelIssue: DashboardSidePanelIssue | null
  /** Makes the list container a labelled region that takes focus when the panel closes without a row to return to. */
  listContainerProps: DashboardListContainerProps
  /** The rendered side panel. */
  sidePanel: ReactNode
  /** Persistent dashboard-level recovery guidance for a deletion that outlived its panel. */
  deletionErrorNotice: {title: string; message: string} | null
}

/**
 * Owns the dashboard issue side-panel behavior: open state, `?issue=` URL sync (shareable +
 * back/forward), j/k navigation between issues, opening sub-issues in place, and the focus/refresh
 * choreography around edits and deletes.
 *
 * Must be called from the route component, not the list content component: the latter is unmounted
 * both by the navigation skeleton and by the list query's suspense, so panel state kept there
 * would be lost on every refresh.
 */
export function useDashboardIssueSidePanel({
  navigableIssues,
  refreshList,
  issueDeletionConnection,
}: UseDashboardIssueSidePanelParams): UseDashboardIssueSidePanelResult {
  const deletions = useIssueDeletionState()
  const [pane, setPane] = useState<{
    issue: DashboardSidePanelIssue | null
    issueDeletionConnection?: IssueDeletionConnection
  }>({issue: null})
  const [deletionErrorNotice, setDeletionErrorNotice] = useState<{title: string; message: string} | null>(null)
  const currentPaneRef = useRef(pane)
  const ownedDeletionIdsRef = useRef(new Set<number>())
  const completedDeletionIdsRef = useRef(new Set<number>())
  const sidePanelIssue = pane.issue
  const [rememberedDeletionConnections] = useState(() => new Map<string, IssueDeletionConnection>())
  const setSidePanelIssue = useCallback(
    (issue: DashboardSidePanelIssue | null, paneIssueDeletionConnection?: IssueDeletionConnection) => {
      const next = {issue, issueDeletionConnection: paneIssueDeletionConnection}
      currentPaneRef.current = next
      setPane(next)
    },
    [],
  )
  // The panel returns focus to the clicked row link when it closes.
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const listContainerRef = useRef<HTMLDivElement | null>(null)
  const focusListOnCloseRef = useRef(false)
  // A list refresh to run when the panel closes. Edits and create defer their refetch here rather
  // than refetch underneath the open panel -- and for create it also gives the new issue time to
  // land in the search index before the list reloads.
  const pendingRefreshRef = useRef(false)

  // Set the open issue and mirror it to the URL (?issue=owner|repo|number) so the panel is
  // shareable and browser back/forward opens/closes it, matching the issues dashboard.
  const commitSidePanelIssue = useCallback(
    (identifier: DashboardSidePanelIssue | null, paneIssueDeletionConnection?: IssueDeletionConnection) => {
      if (identifier) {
        const key = sidePanelIssueKey(identifier)
        if (paneIssueDeletionConnection) {
          rememberedDeletionConnections.delete(key)
          // Keep back/forward ownership recovery bounded; when full, drop the first remembered entry.
          if (rememberedDeletionConnections.size >= MAX_REMEMBERED_DELETION_CONNECTIONS) {
            const firstRememberedKey = rememberedDeletionConnections.keys().next().value
            if (firstRememberedKey) rememberedDeletionConnections.delete(firstRememberedKey)
          }
          rememberedDeletionConnections.set(key, paneIssueDeletionConnection)
        } else {
          rememberedDeletionConnections.delete(key)
        }
      }
      setSidePanelIssue(identifier, paneIssueDeletionConnection)
      writeSidePanelIssueToUrl(identifier)
    },
    [rememberedDeletionConnections, setSidePanelIssue],
  )

  const handleIssueSelect = useCallback(
    (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => {
      // Capture the actual clicked anchor (not document.activeElement, which mouse clicks
      // don't reliably update in Safari/WebKit) so focus returns deterministically on close.
      returnFocusRef.current = returnFocusElement
      // A real clicked row supersedes the created-issue focus fallback (see handleIssueCreated).
      focusListOnCloseRef.current = false
      commitSidePanelIssue(toSidePanelIssue(identifier))
    },
    [commitSidePanelIssue],
  )

  const handleIssueCreated = useCallback(
    ({issue, createMore, accountIssueOrgLogin}: OnCreateProps) => {
      if (createMore) {
        // The dialog stays open (no panel close to defer to), so refresh now -- best effort against
        // search-index lag, since the user isn't looking at the list yet.
        refreshList()
        return
      }
      // A just-created issue often isn't in the search index yet, so an immediate refetch races it
      // and drops the new row. Defer the refresh to panel close (the edit path), by which point
      // indexing has typically caught up, so it shows up and is j/k-navigable.
      pendingRefreshRef.current = true
      // No clicked row to restore focus to, so arm the delete path's list-container fallback so
      // focus isn't dropped to document.body on close (WCAG 2.4.3).
      returnFocusRef.current = null
      focusListOnCloseRef.current = true
      // An org selection means the issue lands in the account's designated repository, so open it as
      // an account issue (matching the list-select shape) to surface the account owner pill instead
      // of the repository pill. The permalink mirrors the backend's `account_issue_permalink`.
      // The account treatment matches the list-select shape when creation started from an org.
      commitSidePanelIssue(
        toSidePanelIssue({
          owner: issue.repository.owner.login,
          repo: issue.repository.name,
          number: issue.number,
          globalId: issue.id,
          ...(accountIssueOrgLogin
            ? {isAccountIssue: true, permalink: `/orgs/${accountIssueOrgLogin}/issues/${issue.number}`}
            : {}),
        }),
      )
    },
    [commitSidePanelIssue, refreshList],
  )

  const navigateToAdjacentIssue = useCallback(
    (delta: 1 | -1) => {
      if (!sidePanelIssue) return
      const accountPermalink = sidePanelIssue.isAccountIssue
        ? normalizeSidePanelIssuePermalink(sidePanelIssue.permalink)
        : null
      const currentIndex = navigableIssues.findIndex(
        item =>
          (accountPermalink !== null &&
            item.isAccountIssue &&
            typeof item.permalink === 'string' &&
            normalizeSidePanelIssuePermalink(item.permalink) === accountPermalink) ||
          (!sidePanelIssue.isAccountIssue &&
            item.number === sidePanelIssue.number &&
            item.owner === sidePanelIssue.owner &&
            item.repo === sidePanelIssue.repo),
      )
      if (currentIndex < 0) return
      const target = navigableIssues[currentIndex + delta]
      if (target) commitSidePanelIssue(toSidePanelIssue(target))
    },
    [commitSidePanelIssue, navigableIssues, sidePanelIssue],
  )

  const handleNavigateToNextItem = useCallback(() => navigateToAdjacentIssue(1), [navigateToAdjacentIssue])
  const handleNavigateToPreviousItem = useCallback(() => navigateToAdjacentIssue(-1), [navigateToAdjacentIssue])

  const handleSubIssueSelect = useCallback(
    (identifier: DashboardIssueIdentifier, paneIssueDeletionConnection?: IssueDeletionConnection) => {
      // Open the clicked sub-issue in the same panel. The originally clicked row stays the
      // focus-return target, matching the issues dashboard.
      commitSidePanelIssue(toSidePanelIssue(identifier), paneIssueDeletionConnection)
    },
    [commitSidePanelIssue],
  )

  // An edit shouldn't refetch the list underneath the open panel: it would cost one request per
  // change for a result the user can't see yet. Record that a refresh is pending and run it once
  // on close. Deletion is the exception: its own settlement already refetches the list, which
  // covers any edits made earlier in the same session.
  const handleIssueChange = useCallback(() => {
    pendingRefreshRef.current = true
  }, [])

  const consumePendingRefresh = useCallback(() => {
    if (!pendingRefreshRef.current) return
    pendingRefreshRef.current = false
    refreshList()
  }, [refreshList])

  // Read from the popstate subscription below, which stays mounted across renders.
  const consumePendingRefreshRef = useRef(consumePendingRefresh)
  useEffect(() => {
    consumePendingRefreshRef.current = consumePendingRefresh
  }, [consumePendingRefresh])
  const currentIssueDeletionConnection = pane.issueDeletionConnection ?? issueDeletionConnection

  const handleSidePanelClose = useCallback(() => {
    commitSidePanelIssue(null)
    consumePendingRefresh()
  }, [commitSidePanelIssue, consumePendingRefresh])

  const handleIssueDelete = useCallback(
    async (issueId: string, options?: DashboardIssueDeleteOptions) => {
      // Shared consumers such as Planning still need their confirmation-only refresh.
      if (!currentIssueDeletionConnection || !options?.alreadySettled) refreshList()
      // Completion belongs to the pane that started deletion, not a later navigation or reopening.
      if (currentPaneRef.current !== pane || !pane.issue) return
      // The deleted row is disappearing, so focus moves to the list container after the panel closes.
      focusListOnCloseRef.current = true
      pendingRefreshRef.current = false
      handleSidePanelClose()
    },
    [currentIssueDeletionConnection, handleSidePanelClose, pane, refreshList],
  )

  const handleIssueDeleteStart = useCallback(() => {
    if (!currentPaneRef.current.issue) return
    setDeletionErrorNotice(null)
    // The deleted row is disappearing, so focus must land on the stable list container.
    focusListOnCloseRef.current = true
    // Settlement refetches the list, which also covers edits made earlier in this pane.
    pendingRefreshRef.current = false
    handleSidePanelClose()
  }, [handleSidePanelClose])

  const handleIssueDeleteError = useCallback(() => {
    setDeletionErrorNotice({title: 'Issue deletion failed', message: DELETE_REJECTED_MESSAGE})
    announce(DELETE_REJECTED_MESSAGE, {assertive: true})
  }, [])

  const handleIssueDeleteCompletionError = useCallback(() => {
    setDeletionErrorNotice({title: 'Issue deleted; refresh failed', message: DELETE_COMPLETION_MESSAGE})
    announce(DELETE_COMPLETION_MESSAGE, {assertive: true})
  }, [])

  useEffect(() => {
    for (const deletion of deletions) {
      if (!ownedDeletionIdsRef.current.has(deletion.id)) {
        const openIssue = currentPaneRef.current.issue
        if (
          !openIssue ||
          (openIssue.globalId
            ? openIssue.globalId !== deletion.issueId
            : openIssue.isAccountIssue
              ? openIssue.number !== deletion.number ||
                new URL(openIssue.permalink, ssrSafeLocation.origin).pathname.split('/')[2] !== deletion.owner
              : openIssue.owner !== deletion.owner ||
                openIssue.repo !== deletion.repository ||
                openIssue.number !== deletion.number)
        ) {
          continue
        }
        ownedDeletionIdsRef.current.add(deletion.id)
        handleIssueDeleteStart()
      }
      if (deletion.status === 'pending' || completedDeletionIdsRef.current.has(deletion.id)) continue
      completedDeletionIdsRef.current.add(deletion.id)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Observe the external mutation cache after the pane unmounts.
      if (deletion.status === 'error') handleIssueDeleteError()
      else if (deletion.completionError) handleIssueDeleteCompletionError()
    }
  }, [deletions, handleIssueDeleteCompletionError, handleIssueDeleteError, handleIssueDeleteStart])

  useEffect(() => {
    if (sidePanelIssue) return
    const returnFocusElement = returnFocusRef.current
    if (!focusListOnCloseRef.current && (!returnFocusElement || returnFocusElement.isConnected)) return
    focusListOnCloseRef.current = false
    return focusParentListTarget(listContainerRef.current)
  }, [sidePanelIssue])

  // Open the panel from the URL on mount (client-only, so SSR renders it closed) and keep it in
  // sync with browser back/forward. This intentionally uses an effect:
  // it hydrates initial state from the URL on mount and subscribes to the history `popstate`
  // store.
  useEffect(() => {
    // A `?issue=` URL opens the panel with no clicked row (an initial deep link or Back/Forward), so
    // arm the list-container focus fallback when there's no return target -- as the create path does
    // -- to avoid dropping focus to document.body on close (WCAG 2.4.3).
    const initialIssue = parseSidePanelIssueFromUrl()
    if (initialIssue && !returnFocusRef.current) focusListOnCloseRef.current = true
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate initial open issue from the URL on mount and follow popstate
    setSidePanelIssue(
      initialIssue,
      initialIssue ? rememberedDeletionConnections.get(sidePanelIssueKey(initialIssue)) : undefined,
    )

    const onPopState = () => {
      const identifier = parseSidePanelIssueFromUrl()
      if (identifier && !returnFocusRef.current) focusListOnCloseRef.current = true
      setSidePanelIssue(
        identifier,
        identifier ? rememberedDeletionConnections.get(sidePanelIssueKey(identifier)) : undefined,
      )
      // Backing out of an edited issue closes the panel without going through the close handler,
      // so run the deferred refresh here. The URL already reflects the state history restored,
      // so this must not write another entry.
      if (!identifier) consumePendingRefreshRef.current()
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      currentPaneRef.current = {issue: null}
      rememberedDeletionConnections.clear()
      window.removeEventListener('popstate', onPopState)
    }
  }, [rememberedDeletionConnections, setSidePanelIssue])

  const listContainerProps = useMemo<DashboardListContainerProps>(
    () => ({ref: listContainerRef, role: 'region', 'aria-label': 'Issues'}),
    [],
  )
  return {
    onIssueSelect: handleIssueSelect,
    onIssueCreated: handleIssueCreated,
    isSidePanelOpen: sidePanelIssue !== null,
    sidePanelIssue,
    listContainerProps,
    deletionErrorNotice,
    sidePanel: (
      <DashboardIssueSidePanel
        issue={sidePanelIssue}
        onClose={handleSidePanelClose}
        onIssueChange={handleIssueChange}
        onIssueDelete={handleIssueDelete}
        hostReportsIssueDeletionErrors
        issueDeletionConnection={currentIssueDeletionConnection}
        onNavigateToNextItem={handleNavigateToNextItem}
        onNavigateToPreviousItem={handleNavigateToPreviousItem}
        onSubIssueSelect={handleSubIssueSelect}
        returnFocusRef={returnFocusRef}
      />
    ),
  }
}
